import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove } from '@dnd-kit/sortable'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { ScatterPlotMetadata, CellPlotData } from 'redux/slices/types'
import DraggableGridTile from './DraggableGridTile'
import { calculateGridSections, convertToViewportPercentages } from './gridPositionUtils'
import { calculatePlotRangeWithPadding } from './utils'

interface GridTileOverlayProps {
  scatterPlotMetaData: ScatterPlotMetadata
  plotHeight: number
  plotData: CellPlotData[]
}

const GridTileOverlay: React.FC<GridTileOverlayProps> = ({
  scatterPlotMetaData,
  plotHeight,
  plotData,
}) => {
  const {
    visibleSelectedComparisonDimensions,
    setSelectedComparisonDimensions,
    cellVisualizations: { groupCompare },
  } = useCellVisualizationsSlice()

  // Get drag and drop enabled state, defaulting to false
  const dragDropEnabled = groupCompare?.dragDropEnabled ?? false

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
        tolerance: 5,
        delay: 0,
      },
    }),
    useSensor(KeyboardSensor)
  )

  // Use different sensors based on drag/drop enabled state
  const activeSensors = dragDropEnabled ? sensors : []

  // State to track Plotly's actual range (triggers re-render when available)
  const [plotlyRangeX, setPlotlyRangeX] = useState<[number, number] | null>(null)

  // Get Plotly's actual range from the DOM
  const getPlotlyActualRange = useCallback((): [number, number] | null => {
    const plotDiv = document.getElementById('scatter-plot') as HTMLElement & {
      layout?: { xaxis?: { range?: number[] } }
    }
    if (plotDiv?.layout?.xaxis?.range) {
      return plotDiv.layout.xaxis.range as [number, number]
    }
    return null
  }, [])

  // Update range state from Plotly's actual values
  const updateRangeFromPlotly = useCallback(() => {
    const range = getPlotlyActualRange()
    if (range) {
      setPlotlyRangeX((prev) => {
        if (!prev || prev[0] !== range[0] || prev[1] !== range[1]) {
          return range
        }
        return prev
      })
    }
  }, [getPlotlyActualRange])

  // Listen to Plotly events for range updates instead of using timeouts
  useEffect(() => {
    const plotDiv = document.getElementById('scatter-plot')
    if (!plotDiv) return undefined

    // Check initial range
    updateRangeFromPlotly()

    // Listen for Plotly events
    const handlePlotlyEvent = () => {
      updateRangeFromPlotly()
    }

    // plotly_afterplot fires after rendering completes
    // plotly_relayout fires when layout changes (zoom, pan, autorange)
    plotDiv.addEventListener('plotly_afterplot', handlePlotlyEvent)
    plotDiv.addEventListener('plotly_relayout', handlePlotlyEvent)

    return () => {
      plotDiv.removeEventListener('plotly_afterplot', handlePlotlyEvent)
      plotDiv.removeEventListener('plotly_relayout', handlePlotlyEvent)
    }
  }, [plotData, visibleSelectedComparisonDimensions, updateRangeFromPlotly])

  // Force re-read of Plotly range when drag-drop toggle changes
  // This fixes stale position measurements when toggling from single-UMAP to multiplot view
  useEffect(() => {
    if (dragDropEnabled) {
      // Allow Plotly to finish any pending layout updates before reading range
      const frameId = requestAnimationFrame(() => {
        updateRangeFromPlotly()
      })
      return () => cancelAnimationFrame(frameId)
    }
    return undefined
  }, [dragDropEnabled, updateRangeFromPlotly])

  const getGridTilePositions = () => {
    if (!visibleSelectedComparisonDimensions || !scatterPlotMetaData || !plotData.length) return []

    // Use Plotly's ACTUAL range for accurate overlay positioning
    // Fall back to calculated range if Plotly hasn't rendered yet
    const { rangeX: calculatedRangeX } = calculatePlotRangeWithPadding(plotData)
    const rangeX = plotlyRangeX || calculatedRangeX

    const gridSections = calculateGridSections(
      scatterPlotMetaData,
      visibleSelectedComparisonDimensions
    )

    return convertToViewportPercentages(gridSections, scatterPlotMetaData, rangeX)
  }

  const tilePositions = getGridTilePositions()

  // Ref for the overlay container
  const overlayRef = useRef<HTMLDivElement>(null)

  const [isReordering, setIsReordering] = useState(false)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      setIsReordering(false)
      return
    }

    const activeTileId = active.id as string
    const overTileId = over.id as string

    // Find the tile positions to get actual indices
    const activeTile = tilePositions.find((pos) => pos.id === activeTileId)
    const overTile = tilePositions.find((pos) => pos.id === overTileId)

    if (!activeTile || !overTile) {
      setIsReordering(false)
      return
    }

    // Only allow reordering within the same dimension
    if (
      activeTile.dimensionIndex === overTile.dimensionIndex &&
      visibleSelectedComparisonDimensions
    ) {
      const updatedDimensions = [...visibleSelectedComparisonDimensions]
      const dimension = updatedDimensions[activeTile.dimensionIndex]

      if (dimension && activeTile.valueIndex !== overTile.valueIndex) {
        setIsReordering(true)

        // Defer the expensive operation to next frame so the message renders first
        requestAnimationFrame(() => {
          const oldIndex = activeTile.valueIndex
          const newIndex = overTile.valueIndex

          // Use dnd-kit's arrayMove which handles index shifting correctly
          const newValues = arrayMove(dimension.values, oldIndex, newIndex)

          updatedDimensions[activeTile.dimensionIndex] = {
            ...dimension,
            values: newValues,
          }

          setSelectedComparisonDimensions(updatedDimensions)
        })
      }
    }
  }

  const tileIds = tilePositions.map((pos) => pos.id)

  // Reset reordering state when dimensions update (plot has finished recalculating)
  useEffect(() => {
    setIsReordering(false)
  }, [visibleSelectedComparisonDimensions])

  // Don't render if no comparison dimensions
  if (!visibleSelectedComparisonDimensions?.length) {
    return null
  }

  return (
    <Box
      ref={overlayRef}
      data-testid="grid-tile-overlay"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${plotHeight}px`,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {/* Reordering indicator */}
      {isReordering && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            zIndex: 100,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Reordering...
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <DndContext
          sensors={activeSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={tileIds}>
            {tilePositions.map((position) => (
              <DraggableGridTile
                key={position.id}
                id={position.id}
                position={{
                  left: position.left,
                  top: position.top,
                  width: position.width,
                  height: position.height,
                }}
                label={position.label}
                dragEnabled={dragDropEnabled}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  )
}

export default GridTileOverlay
